import "@/i18n/i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "modern-normalize/modern-normalize.css";
import React, { Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";
import FinishGoogleAuth from "./components/FinishGoogleAuth";
import Loading from "./components/Loading";
import Login from "./components/Login";
import PasswordRecovery from "./components/PasswordRecovery";
import SignUp from "./components/SignUp";
import ErrorPage from "./error-page";
import "./fonts";
import "./index.css";
import { isUserFromRussia } from "./lib/utils";
import reportWebVitals from "./reportWebVitals";
import {
  FINISH_GOOGLE_AUTH_ROUTE,
  LOGIN_ROUTE,
  ONBOARDING_ROUTE,
  INSTRUMENT_ROUTE,
  RECOVER_PASSWORD_ROUTE,
  REGISTER_ROUTE,
} from "./shared/constants";
import Onboarding from "./components/Onboarding";
import SectionOfInstrument from "./components/SectionOfInstrument";

const Root = React.lazy(() => import("./routes/Root"));

const LocationAwareRedirect = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

// Component that conditionally renders FinishGoogleAuth based on user location
const ConditionalFinishGoogleAuth = () => {
  const [isRussianUser, setIsRussianUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    isUserFromRussia().then((isRussian) => {
      setIsRussianUser(isRussian);
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (isRussianUser) {
    return <Navigate to={LOGIN_ROUTE} replace />;
  }

  return <FinishGoogleAuth />;
};

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: `/${ONBOARDING_ROUTE}`,
    element: <Onboarding />,
    errorElement: <ErrorPage />,
  },
  {
    path: `/${INSTRUMENT_ROUTE}`,
    element: <SectionOfInstrument />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/",
    element: <Root />,
    children: [
      {
        index: true,
        element: <LocationAwareRedirect to={LOGIN_ROUTE} />,
      },
      { path: LOGIN_ROUTE, element: <Login /> },
      { path: REGISTER_ROUTE, element: <SignUp /> },
      { path: RECOVER_PASSWORD_ROUTE, element: <PasswordRecovery /> },
      {
        path: FINISH_GOOGLE_AUTH_ROUTE,
        element: <ConditionalFinishGoogleAuth />,
      },
    ],
    errorElement: <ErrorPage />,
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<Loading />}>
        <RouterProvider router={router} />
      </Suspense>
    </QueryClientProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
